import { Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { Account } from '../../../shared/account.model';
import { Category } from '../../../../category/shared/category.model';
import { ChartOptions, ChartType, ChartDataSets, ChartPoint } from 'chart.js';
import { Label, BaseChartDirective } from 'ng2-charts';
import * as _moment from 'moment';
// tslint:disable-next-line:no-duplicate-imports
import { default as _rollupMoment } from 'moment';

const moment = _rollupMoment || _moment;

@Component({
  selector: 'app-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss']
})
export class LineChartComponent implements OnChanges {

  @Input() month?: string;
  @Input() accounts?: Account[];
  @Input() categories?: Category[];
  @Input() displayedLabels: string[] = [];
  @Output() legendClicked = new EventEmitter<string[]>();
  @ViewChild(BaseChartDirective, { static: true }) baseChart?: BaseChartDirective;

  lineChartOptions: ChartOptions = {
    responsive: true,
    scales: {
      xAxes: [{
        type: 'time',
        time: {
          unit: 'day',
          displayFormats: {
            day: 'M/D'
          },
          tooltipFormat: 'll'
        },
        ticks: {
          fontColor: '#999'
        },
        gridLines: {
          color: '#999'
        }
      }],
      yAxes: [{
        id: 'primary',
        ticks: {
          beginAtZero: true,
          fontColor: '#999',
          callback: (label) => {
            return `\xA5${String(label).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')}`;
          }
        },
        gridLines: {
          color: '#999'
        }
      }, {
        id: 'secondary',
        position: 'right',
        ticks: {
          beginAtZero: true,
          fontColor: '#999',
          callback: (label) => {
            return `\xA5${String(label).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')}`;
          }
        }
      }]
    },
    tooltips: {
      callbacks: {
        title: (tooltipItems, _data) => {
          return tooltipItems ? moment(new Date(tooltipItems[0].xLabel || '')).format('YYYY/M/DD') : '';
        },
        label: (tooltipItem, data) => {
          if (tooltipItem && data.datasets) {
            const dataset = data.datasets[tooltipItem.datasetIndex || 0];
            const label = dataset.label;
            const x = (dataset.data || [])[tooltipItem.index || 0];
            let currentValue;
            if (x && typeof x === 'object' && 'y' in x) {
              currentValue = x.y;
            } else {
              currentValue = x;
            }
            return `${label} : \xA5${(currentValue || 0).toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')} `;
          } else {
            return '';
          }
        },
      }
    },
    plugins: {}
  };
  lineChartLabels: Label[] = [];
  lineChartData: ChartDataSets[] = [{ data: [] }];
  lineChartType: ChartType = 'line';
  lineChartLegend = false;
  lineChartPlugins = [];
  lineChartColors = [];

  constructor() { }

  ngOnChanges() {
    if (this.accounts && this.categories && this.baseChart && this.lineChartOptions && this.lineChartOptions.scales &&
      this.lineChartOptions.scales.xAxes && this.lineChartOptions.scales.xAxes[0].time) {

      let minDate = moment().startOf('month');
      try {
        if (this.month) {
          minDate = moment(this.month, 'YYYY-MM');
        }
      } catch { }
      const maxDate = minDate.clone().endOf('month');
      const minDateString = minDate.format('YYYY-MM-DD');
      const maxDateString = maxDate.format('YYYY-MM-DD');

      const totalCategory = '合計';

      const hash: { [key: string]: { [key: string]: number } } = this.accounts
        .filter(accounts => this.displayedLabels.length ? this.displayedLabels
          .some(dl => dl === (accounts.category && accounts.category.name || '')) : true)
        .map(account => (
          {
            paidOn: moment(account.paidOn),
            categoryName: account.category && account.category.name || 'undefined',
            amount: account.amount || 0
          }
        ))
        .reduce((acc: { [key: string]: { [key: string]: number } }, cur) => {
          const dateKey = (cur.paidOn < minDate ? minDate :
            cur.paidOn > maxDate ? maxDate : cur.paidOn).format('YYYY-MM-DD');
          acc[cur.categoryName] = acc[cur.categoryName] || {};
          acc[cur.categoryName][dateKey] = (acc[cur.categoryName][dateKey] || 0) + cur.amount;
          acc[totalCategory] = acc[totalCategory] || {};
          acc[totalCategory][dateKey] = (acc[totalCategory][dateKey] || 0) + cur.amount;
          return acc;
        }, {});

      const lineChartData = Object.keys(hash)
        .map(key => {
          hash[key][minDateString] = hash[key][minDateString] || 0;
          hash[key][maxDateString] = hash[key][maxDateString] || 0;
          return { key, value: hash[key] };
        })
        .map(x => ({
          label: x.key,
          data: Object.keys(x.value).map(dateKey => ({ t: dateKey, y: x.value[dateKey] }))
            .sort((a, b) => moment(a.t) > moment(b.t) ? 1 : -1)
            .reduce((acc: { t: string, y: number }[], cur) => {
              const previous = (acc.slice(-1)[0] || {}).y || 0;
              cur.y = cur.y + previous;
              return [...acc, cur];
            }, []),
        } as ChartDataSets));

      this.lineChartData = [
        ...lineChartData.filter(x => x.label !== totalCategory)
          .map(dataSets => ({
            label: dataSets.label,
            data: dataSets.data,
            yAxisID: 'primary',
            fill: false,
            lineTension: 0,
            borderColor: ((this.categories || []).find(cat => dataSets.label === cat.name) || {}).color,
            pointBackgroundColor: '#999',
            pointHoverBackgroundColor: ((this.categories || []).find(cat => dataSets.label === cat.name) || {}).color,
            pointBorderColor: '#fff',
            pointHoverBorderColor: '#fff',
          })),
        ...lineChartData.filter(x => x.label === totalCategory)
          .map(dataSets => ({
            label: dataSets.label,
            data: dataSets.data,
            yAxisID: 'secondary',
            fill: true,
            lineTension: 0,
            borderColor: 'rgba(0, 0, 0, 0.075)',
            pointBackgroundColor: 'rgba(128, 128, 128, 0.5)',
            pointHoverBackgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointBorderColor: '#fff',
            pointHoverBorderColor: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.075)',
          })),
      ];

      this.lineChartOptions.scales.xAxes[0].time.min = minDateString;
      this.lineChartOptions.scales.xAxes[0].time.max = maxDateString;
      this.baseChart.ngOnChanges({});
    }
  }
}
